/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { type CommonProps } from '../_common/types';
import Neo4JIconBlackIcon from '../icons/generated/custom/Neo4JIconBlack';
import Neo4JIconColorIcon from '../icons/generated/custom/Neo4JIconColor';
import Neo4JIconWhiteIcon from '../icons/generated/custom/Neo4JIconWhite';
import Neo4JLogoBlackIcon from '../icons/generated/custom/Neo4JLogoBlack';
import Neo4JLogoColorIcon from '../icons/generated/custom/Neo4JLogoColor';
import Neo4JLogoWhiteIcon from '../icons/generated/custom/Neo4JLogoWhite';
import { useNeedleTheme } from '../theme';

type IconColor = 'black' | 'white' | 'color';
type IconType = 'full' | 'icon';

type LogoProps = {
  color?: IconColor;
  type?: IconType;
};

const iconMap = {
  full: {
    black: Neo4JLogoBlackIcon,
    color: Neo4JLogoColorIcon,
    white: Neo4JLogoWhiteIcon,
  },
  icon: {
    black: Neo4JIconBlackIcon,
    color: Neo4JIconColorIcon,
    white: Neo4JIconWhiteIcon,
  },
};

/**
 * By default, Logo will be theme aware, unless `color` is explicitly provided.
 *
 * On light theme it will be colored and on dark theme it will be white.
 */
export const Logo = ({
  color,
  type = 'full',
  className,
  style,
  htmlAttributes,
  ref,
}: CommonProps<'svg', LogoProps>) => {
  const theme = useNeedleTheme();

  // use themed logo if color is not explicitly provided
  const iconColor: IconColor = color
    ? color
    : theme.theme === 'light'
      ? 'color'
      : 'white';

  const Icon = iconMap[type][iconColor];

  return (
    <Icon ref={ref} className={className} style={style} {...htmlAttributes} />
  );
};
